import { mazeMeta } from "@/data/algorithmMeta";

export const mazeAlgorithmOptions = Object.keys(mazeMeta).map((key) => ({
  value: key,
  label: mazeMeta[key].label,
}));

const createRandom = (seed = Date.now()) => {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const inBounds = (row, col, rows, cols) => row > 0 && col > 0 && row < rows - 1 && col < cols - 1;

const cloneGrid = (grid) => grid.map((row) => [...row]);

export const createMazeGrid = (rows = 21, cols = 35) =>
  Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));

const carve = (grid, row, col) => {
  grid[row][col] = 1;
};

const recordStep = (steps, grid, action, stats) => {
  steps.push({
    grid: cloneGrid(grid),
    action,
    stats: { ...stats },
  });
};

const backtrackingMaze = (rows, cols, rand) => {
  const grid = createMazeGrid(rows, cols);
  const steps = [];
  const stats = { visitedNodes: 0, executionSteps: 0, comparisons: 0, swaps: 0 };

  const stack = [[1, 1]];
  carve(grid, 1, 1);
  recordStep(steps, grid, "Initialize maze root", stats);

  const directions = [
    [0, 2],
    [0, -2],
    [2, 0],
    [-2, 0],
  ];

  while (stack.length) {
    const [row, col] = stack[stack.length - 1];
    stats.visitedNodes += 1;

    const options = directions
      .map(([dr, dc]) => ({
        nr: row + dr,
        nc: col + dc,
        wr: row + dr / 2,
        wc: col + dc / 2,
      }))
      .filter(({ nr, nc }) => inBounds(nr, nc, rows, cols) && grid[nr][nc] === 0);

    if (options.length === 0) {
      stack.pop();
      continue;
    }

    const next = options[Math.floor(rand() * options.length)];
    stats.comparisons += 1;
    carve(grid, next.wr, next.wc);
    carve(grid, next.nr, next.nc);
    stack.push([next.nr, next.nc]);
    stats.swaps += 1;

    if (steps.length % 2 === 0) {
      recordStep(steps, grid, `Carve corridor to ${next.nr},${next.nc}`, stats);
    }
  }

  grid[rows - 2][cols - 2] = 1;
  recordStep(steps, grid, "Maze generation complete", stats);
  stats.executionSteps = steps.length;
  return { steps, finalGrid: grid, stats };
};

const primMaze = (rows, cols, rand) => {
  const grid = createMazeGrid(rows, cols);
  const steps = [];
  const stats = { visitedNodes: 0, executionSteps: 0, comparisons: 0, swaps: 0 };
  const frontier = [];

  const addFrontier = (row, col) => {
    [
      [0, 2],
      [0, -2],
      [2, 0],
      [-2, 0],
    ].forEach(([dr, dc]) => {
      const nr = row + dr;
      const nc = col + dc;
      if (inBounds(nr, nc, rows, cols) && grid[nr][nc] === 0) {
        frontier.push([nr, nc, row, col]);
      }
    });
  };

  carve(grid, 1, 1);
  addFrontier(1, 1);
  recordStep(steps, grid, "Seed frontier", stats);

  while (frontier.length) {
    const idx = Math.floor(rand() * frontier.length);
    const [row, col, parentRow, parentCol] = frontier.splice(idx, 1)[0];
    stats.visitedNodes += 1;
    if (grid[row][col] === 1) {
      continue;
    }
    carve(grid, row, col);
    carve(grid, Math.floor((row + parentRow) / 2), Math.floor((col + parentCol) / 2));
    addFrontier(row, col);
    stats.swaps += 1;
    stats.comparisons += 1;

    if (steps.length % 3 === 0) {
      recordStep(steps, grid, `Add cell ${row},${col} from frontier`, stats);
    }
  }

  grid[rows - 2][cols - 2] = 1;
  recordStep(steps, grid, "Prim maze complete", stats);
  stats.executionSteps = steps.length;
  return { steps, finalGrid: grid, stats };
};

const kruskalMaze = (rows, cols, rand) => {
  const grid = createMazeGrid(rows, cols);
  const steps = [];
  const stats = { visitedNodes: 0, executionSteps: 0, comparisons: 0, swaps: 0 };
  const cells = [];
  const parent = {};

  for (let row = 1; row < rows; row += 2) {
    for (let col = 1; col < cols; col += 2) {
      const key = `${row},${col}`;
      parent[key] = key;
      cells.push({ row, col, key });
      carve(grid, row, col);
    }
  }

  const find = (key) => {
    if (parent[key] !== key) {
      parent[key] = find(parent[key]);
    }
    return parent[key];
  };

  const union = (leftKey, rightKey) => {
    const leftRoot = find(leftKey);
    const rightRoot = find(rightKey);
    if (leftRoot === rightRoot) {
      return false;
    }
    parent[rightRoot] = leftRoot;
    return true;
  };

  const edges = [];
  cells.forEach(({ row, col, key }) => {
    if (row + 2 < rows) {
      edges.push({
        leftKey: key,
        rightKey: `${row + 2},${col}`,
        wallRow: row + 1,
        wallCol: col,
        nextRow: row + 2,
        nextCol: col,
        weight: rand(),
      });
    }
    if (col + 2 < cols) {
      edges.push({
        leftKey: key,
        rightKey: `${row},${col + 2}`,
        wallRow: row,
        wallCol: col + 1,
        nextRow: row,
        nextCol: col + 2,
        weight: rand(),
      });
    }
  });

  edges.sort((left, right) => left.weight - right.weight);
  recordStep(steps, grid, "Initialize Kruskal cell sets", stats);

  edges.forEach((edge, index) => {
    stats.comparisons += 1;
    stats.visitedNodes += 1;

    if (!union(edge.leftKey, edge.rightKey)) {
      return;
    }

    carve(grid, edge.wallRow, edge.wallCol);
    stats.swaps += 1;

    if (index % 2 === 0) {
      recordStep(steps, grid, `Join sets through ${edge.nextRow},${edge.nextCol}`, stats);
    }
  });

  grid[1][1] = 1;
  grid[rows - 2][cols - 2] = 1;
  recordStep(steps, grid, "Kruskal maze complete", stats);
  stats.executionSteps = steps.length;
  return { steps, finalGrid: grid, stats };
};

const divide = (grid, top, left, bottom, right, steps, stats, rand) => {
  if (bottom - top < 2 || right - left < 2) {
    return;
  }

  const horizontal = bottom - top > right - left;
  if (horizontal) {
    const wallRow = top + 2 * Math.floor(rand() * ((bottom - top) / 2)) + 1;
    const passageCol = left + 2 * Math.floor(rand() * ((right - left + 1) / 2));
    for (let c = left; c <= right; c += 1) {
      if (c !== passageCol) grid[wallRow][c] = 0;
    }
    stats.swaps += 1;
    recordStep(steps, grid, `Horizontal split at row ${wallRow}`, stats);
    divide(grid, top, left, wallRow - 1, right, steps, stats, rand);
    divide(grid, wallRow + 1, left, bottom, right, steps, stats, rand);
  } else {
    const wallCol = left + 2 * Math.floor(rand() * ((right - left) / 2)) + 1;
    const passageRow = top + 2 * Math.floor(rand() * ((bottom - top + 1) / 2));
    for (let r = top; r <= bottom; r += 1) {
      if (r !== passageRow) grid[r][wallCol] = 0;
    }
    stats.swaps += 1;
    recordStep(steps, grid, `Vertical split at col ${wallCol}`, stats);
    divide(grid, top, left, bottom, wallCol - 1, steps, stats, rand);
    divide(grid, top, wallCol + 1, bottom, right, steps, stats, rand);
  }
};

const recursiveDivisionMaze = (rows, cols, rand) => {
  const grid = Array.from({ length: rows }, (_, row) =>
    Array.from({ length: cols }, (_, col) => (row % 2 === 1 && col % 2 === 1 ? 1 : 0)),
  );
  const steps = [];
  const stats = { visitedNodes: 0, executionSteps: 0, comparisons: 0, swaps: 0 };
  recordStep(steps, grid, "Initialize recursive division board", stats);
  divide(grid, 1, 1, rows - 2, cols - 2, steps, stats, rand);
  grid[1][1] = 1;
  grid[rows - 2][cols - 2] = 1;
  recordStep(steps, grid, "Division maze complete", stats);
  stats.executionSteps = steps.length;
  return { steps, finalGrid: grid, stats };
};

export const runMazeGenerator = ({ rows = 21, cols = 35, algorithm = "backtracking", seed = Date.now() }) => {
  const rand = createRandom(seed);
  if (algorithm === "prim") return primMaze(rows, cols, rand);
  if (algorithm === "division") return recursiveDivisionMaze(rows, cols, rand);
  if (algorithm === "kruskal") return kruskalMaze(rows, cols, rand);
  return backtrackingMaze(rows, cols, rand);
};
